// TanStack Query wrappers for /v1/knowledge.
//
// Same caching philosophy as useDatasets: list staleTime 30s, mutations
// invalidate the list. No polling — knowledge_objects don't change
// out-of-band.

import {
  type CreateKnowledgeBody,
  type KnowledgeListResponse,
  type KnowledgeObject,
  type ListKnowledgeParams,
  type PatchKnowledgeBody,
  createKnowledge,
  deleteKnowledge,
  getKnowledge,
  listKnowledge,
  patchKnowledge,
} from '@/lib/knowledge';
import {
  type UseMutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';

export const KNOWLEDGE_QUERY_KEY = ['knowledge'] as const;
export const KNOWLEDGE_ITEM_QUERY_KEY = (id: string) => ['knowledge', id] as const;

export function useKnowledgeList(params: ListKnowledgeParams = {}) {
  return useQuery<KnowledgeListResponse, AxiosError>({
    queryKey: [...KNOWLEDGE_QUERY_KEY, params],
    queryFn: () => listKnowledge(params),
    staleTime: 30_000,
  });
}

export function useKnowledge(id: string | undefined) {
  return useQuery<KnowledgeObject, AxiosError>({
    queryKey: id ? KNOWLEDGE_ITEM_QUERY_KEY(id) : ['knowledge', 'noop'],
    queryFn: () => getKnowledge(id as string),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateKnowledge(
  options?: Omit<
    UseMutationOptions<KnowledgeObject, AxiosError, CreateKnowledgeBody>,
    'mutationFn'
  >,
) {
  const qc = useQueryClient();
  // Spread caller options FIRST so our wrapped onSuccess wins.
  return useMutation<KnowledgeObject, AxiosError, CreateKnowledgeBody>({
    mutationFn: (body) => createKnowledge(body),
    ...options,
    onSuccess: (ko, ...rest) => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_QUERY_KEY });
      qc.setQueryData(KNOWLEDGE_ITEM_QUERY_KEY(ko.id), ko);
      options?.onSuccess?.(ko, ...rest);
    },
  });
}

export interface PatchKnowledgeVariables {
  id: string;
  body: PatchKnowledgeBody;
}

export function usePatchKnowledge(
  options?: Omit<
    UseMutationOptions<KnowledgeObject, AxiosError, PatchKnowledgeVariables>,
    'mutationFn'
  >,
) {
  const qc = useQueryClient();
  return useMutation<KnowledgeObject, AxiosError, PatchKnowledgeVariables>({
    mutationFn: ({ id, body }) => patchKnowledge(id, body),
    ...options,
    onSuccess: (ko, ...rest) => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_QUERY_KEY });
      qc.setQueryData(KNOWLEDGE_ITEM_QUERY_KEY(ko.id), ko);
      options?.onSuccess?.(ko, ...rest);
    },
  });
}

export function useDeleteKnowledge() {
  const qc = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: (id) => deleteKnowledge(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KNOWLEDGE_QUERY_KEY });
    },
  });
}
